import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ArrowLeft, MessageCircle, Phone, Search, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { RiskBanner, RiskFooter } from "@/components/RiskDisclaimer";

const FAQS = [
  {
    q: "What is TradingoX?",
    a: `TradingoX is a mean-reversion scalping bot. It hunts micro-dips to buy and micro-rips to sell —
banking small, repeatable profits over and over, automatically. The goal is simple: stack tiny wins
throughout the trading day while keeping per-trade risk low.`,
  },
  {
    q: "Who created TradingoX?",
    a: `TradingoX was created by Charles Cawv Thao — an entrepreneur, inventor, and seasoned trader —
in September 2026.`,
  },
  {
    q: "Does TradingoX guarantee my trades will be profitable?",
    a: `No. We do not guarantee profits, and nobody honestly can. Every trader's outcome is individual —
no two accounts trade exactly alike. Trading is risky and you should only trade money you can afford to lose.`,
  },
  {
    q: "What stocks does TradingoX trade?",
    a: `Because our strategy banks on small, fast wins, we focus on highly liquid US large-caps and ETFs:`,
    list: [
      "AAPL — Apple",
      "MSFT — Microsoft",
      "NVDA — Nvidia",
      "TSLA — Tesla",
      "SPY — S&P 500 ETF",
      "IBM — IBM",
    ],
    a2: "You can also add any US-listed stock or ETF that Alpaca supports through your Dashboard watchlist. We're continuously scouting for more.",
  },
  {
    q: "What broker does TradingoX use?",
    a: `We recommend and integrate directly with `,
    linkText: "Alpaca (alpaca.markets)",
    linkHref: "https://alpaca.markets",
    a2: "Why Alpaca?",
    list: [
      "Commission-free stock and ETF trading",
      "Paper-trading mode (test with zero risk before going live)",
      "Clean REST API that lets TradingoX place orders on your behalf",
      "Alpaca keeps custody of your funds — TradingoX never holds your money",
      "Simple, user-friendly onboarding",
    ],
  },
  {
    q: "Does the broker, Alpaca charge fees to transfer money in or out of my account?",
    a: `No, Alpaca does not charge a fee to deposit or withdraw funds via Automated Clearing House (ACH).

For a full list of brokerage fees, please see `,
    linkText: "Alpaca Securities Brokerage Fee Schedule",
    linkHref: "https://files.alpaca.markets/disclosures/library/AlpacaSecuritiesFeeSchedule.pdf",
  },
  {
    q: "Can anybody trade with TradingoX?",
    a: `Absolutely. TradingoX is designed for seasoned traders, novices, and complete beginners.
The bot handles the execution so you don't need years of chart-reading experience to get started — but you
should still read our full risk disclaimer before trading real money.`,
  },
  {
    q: "What is the minimum deposit required to open an account and start trading?",
    a: `Alpaca, the broker used with TradingoX, does not generally require a minimum deposit to open an eligible live individual brokerage/API account.

However, TradingoX recommends starting with approximately $100–$300 or more to provide enough available capital for the system to operate and to create a more practical live-trading experience. This is a TradingoX recommendation, not a guarantee of performance or profitability.

Important Risk Notice:`,
    list: [
      "Trading and investing involve substantial risk, including the possible loss of your entire investment. Only trade with money you can afford to lose.",
      "Do not borrow money, use emergency funds, or risk your life savings to trade or invest.",
      "Start with an amount that is appropriate for your personal financial situation and risk tolerance.",
      "There is no guarantee of profit or protection against loss.",
    ],
  },
  {
    q: "What does it cost to join?",
    a: `Membership is completely FREE. There is no signup cost, no monthly fee, and no hidden charges.
We only take a 1.25% fee on profitable trades — and only on the net profit. If a trade loses money,
you pay nothing. It's a pure win-share model: we succeed only when you succeed.`,
  },
  {
    q: "Do you pay a referral commission?",
    a: `Not at this time — we're working on a cash referral program for a future release.
However, both you and every friend you refer earn 7 fee-free trading days once they close their
first profitable trade. Details in your Dashboard → Refer & Win.`,
  },
  {
    q: "If I have more questions, who can I turn to?",
    a: `You have two easy options:`,
    channels: [
      { icon: "chat", label: "Chat with Maiv, our virtual assistant", detail: "Bottom-right of every page" },
      { icon: "phone", label: "Text us anytime", detail: "901-361-9173" },
    ],
  },
];

function ChannelPill({ icon, label, detail }) {
  const Icon = icon === "phone" ? Phone : MessageCircle;
  return (
    <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-[#0D0E14] px-4 py-3">
      <span className="grid place-items-center w-9 h-9 rounded-full bg-emerald-500/15 text-emerald-300 shrink-0">
        <Icon className="w-4 h-4" />
      </span>
      <div className="text-sm">
        <div className="font-semibold text-slate-100">{label}</div>
        <div className="text-slate-400 text-xs">{detail}</div>
      </div>
    </div>
  );
}

function FaqItem({ item, index, forceOpen = null }) {
  const [openLocal, setOpenLocal] = useState(index === 0);
  const open = forceOpen === null ? openLocal : forceOpen;
  return (
    <div
      data-testid={`faq-item-${index}`}
      className="rounded-2xl border border-white/10 bg-[#0F111A]/60 backdrop-blur-sm hover:border-emerald-400/30 transition"
    >
      <button
        data-testid={`faq-toggle-${index}`}
        onClick={() => forceOpen === null && setOpenLocal((o) => !o)}
        className="w-full text-left flex items-start justify-between gap-4 px-5 py-4 sm:px-6 sm:py-5"
        aria-expanded={open}
      >
        <div className="flex-1">
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-emerald-300 mb-1">
            Q{String(index + 1).padStart(2, "0")}
          </div>
          <div className="text-base sm:text-lg font-semibold text-slate-100 leading-snug">{item.q}</div>
        </div>
        <ChevronDown
          className={`w-5 h-5 shrink-0 text-slate-400 mt-1 transition-transform ${open ? "rotate-180 text-emerald-300" : ""}`}
        />
      </button>
      {open && (
        <div className="px-5 pb-5 sm:px-6 sm:pb-6 pt-0 space-y-3 text-[15px] text-slate-300 leading-relaxed">
          {String(item.a || "").split(/\n\n+/).map((para, i, arr) => (
            <p key={`${item.q}-p-${i}`}>
              {para.split("\n").map((line, j, lines) => (
                <React.Fragment key={`${item.q}-p-${i}-l-${j}`}>
                  {line}
                  {j < lines.length - 1 && <br />}
                </React.Fragment>
              ))}
              {i === arr.length - 1 && item.linkText && (
                <a
                  href={item.linkHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-emerald-300 underline underline-offset-2 hover:text-emerald-200 font-semibold"
                >
                  {item.linkText}
                </a>
              )}
              {i === arr.length - 1 && item.linkText && "."}
            </p>
          ))}
          {item.list && (
            <ul className="space-y-1.5 pl-1">
              {item.list.map((li) => (
                <li key={li} className="flex items-start gap-2">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0" />
                  <span>{li}</span>
                </li>
              ))}
            </ul>
          )}
          {item.a2 && <p>{item.a2}</p>}
          {item.channels && (
            <div className="grid sm:grid-cols-2 gap-3 pt-2">
              {item.channels.map((c) => (
                <ChannelPill key={c.label} {...c} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function FaqPage() {
  const [query, setQuery] = useState("");
  const q = query.trim().toLowerCase();

  const filtered = useMemo(() => {
    if (!q) return FAQS.map((item, idx) => ({ item, idx }));
    return FAQS
      .map((item, idx) => ({ item, idx }))
      .filter(({ item }) => {
        const haystack = [
          item.q,
          item.a || "",
          item.a2 || "",
          ...(item.list || []),
          ...(item.channels || []).flatMap((c) => [c.label, c.detail]),
          item.linkText || "",
        ]
          .join(" ")
          .toLowerCase();
        return haystack.includes(q);
      });
  }, [q]);

  return (
    <div className="min-h-screen bg-[#0B0C12] text-slate-100 flex flex-col">
      <RiskBanner />
      {/* Header */}
      <header className="max-w-5xl w-full mx-auto px-6 py-5 flex items-center justify-between">
        <Link
          to="/"
          data-testid="faq-back-home"
          className="flex items-center gap-3 group"
        >
          <img
            src="/brand/tradingox-logo.png"
            alt="TradingoX"
            className="w-10 h-10 rounded-full ring-1 ring-white/10 shadow-[0_0_20px_rgba(16,185,129,0.25)]"
          />
          <div className="font-display text-lg font-bold tracking-tight">TradingoX</div>
        </Link>
        <Link
          to="/"
          data-testid="faq-back-btn"
          className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-slate-100 transition"
        >
          <ArrowLeft className="w-4 h-4" /> Back home
        </Link>
      </header>

      <main className="max-w-3xl w-full mx-auto px-6 pt-8 pb-16 flex-1">
        <div className="mb-10 text-center">
          <div className="text-[11px] font-mono uppercase tracking-[0.35em] text-emerald-300 mb-2">
            help center
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">
            Frequently Asked <span className="text-emerald-400">Questions</span>
          </h1>
          <p className="mt-3 text-slate-400 text-base max-w-xl mx-auto">
            Everything you need to know about TradingoX — how it works, what it costs, and how to get started.
          </p>
        </div>

        <div className="mb-8">
          <div className="relative">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
            <Input
              data-testid="faq-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search FAQs — e.g. fees, broker, referral…"
              className="pl-10 pr-10 h-12 bg-[#0F111A]/70 border-white/10 focus-visible:ring-emerald-400/40 text-base"
            />
            {query && (
              <button
                data-testid="faq-search-clear"
                onClick={() => setQuery("")}
                aria-label="Clear search"
                className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 grid place-items-center rounded-full text-slate-400 hover:text-white hover:bg-white/10"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          {q && (
            <div data-testid="faq-search-status" className="mt-2 text-xs text-slate-400">
              {filtered.length === 0
                ? "No answers matched — try a different keyword or chat with Maiv."
                : `${filtered.length} result${filtered.length === 1 ? "" : "s"} for "${query}"`}
            </div>
          )}
        </div>

        <div className="space-y-3" data-testid="faq-list">
          {filtered.map(({ item, idx }) => (
            <FaqItem key={idx} item={item} index={idx} forceOpen={q ? true : null} />
          ))}
        </div>

        <div className="mt-14 rounded-2xl border border-emerald-400/20 bg-gradient-to-br from-emerald-500/10 via-transparent to-transparent p-6 text-center">
          <div className="text-sm text-slate-400 mb-1">Still stuck?</div>
          <div className="font-display text-xl font-bold">Maiv is one click away, or text us at 901-361-9173.</div>
        </div>
      </main>

      <RiskFooter />
    </div>
  );
}
